/**
 * Sección «Correo aroc@tilatina.com»: tablero, bandeja y configuración del
 * buzón de AROC; segmento `correo`. La configuración la editan sólo
 * administradores (el servidor responde 403 si no) y queda auditada.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface TableroCorreo {
  dias: number;
  por_dia: { dia: string; desenlace: string; n: number }[];
  borradores: Record<string, number>;
  mediana_lectura_a_borrador_s: number | null;
  mediana_borrador_a_decision_s: number | null;
  gasto: {
    tokens_dia: number;
    tokens_mes: number;
    usd_dia: number;
    usd_mes: number;
    tope_tokens_dia: number;
    tope_tokens_mes: number;
    tope_usd_dia: number;
    tope_usd_mes: number;
  };
  /** FIX de Banxico vigente para pasar el gasto a pesos; null = no hay. */
  tipo_cambio?: {
    rate: number;
    fuente: string;
    fecha: string | null;
    es_de_hoy: boolean;
  } | null;
  buzon: Record<string, string | boolean>;
}

export interface FilaBandeja {
  id: string;
  recibido_at: string;
  de: string;
  asunto: string;
  desenlace: string;
  caso: string | null;
  veredicto: string | null;
  tokens: number;
  costo_usd: number | null;
  aprobacion_id: string | null;
  estado: string | null;
}

export interface DetalleCorreo {
  registro: Record<string, string | number | null>;
  aprobaciones: {
    id: string;
    tipo: string;
    estado: string;
    envio: string | null;
    solicitada: string;
    decidida: string | null;
    via: string | null;
    para: string[] | null;
    cc: string[] | null;
    borrador: string | null;
    evidencia: string[] | null;
    responsable: string | null;
    area: string | null;
    playbook_id: string | null;
    reason: string | null;
  }[];
}

export interface ConfigCorreo {
  solo_lectura: boolean;
  lista_blanca: Record<string, string[]>;
  srps_por_correo: string[];
  responsables: Record<string, { nombre: string; correo: string }>;
  cc_por_caso: Record<string, string[]>;
  topes: Record<string, number>;
  banderas: Record<string, string | boolean | null>;
  como_se_cambia: Record<string, string>;
}

const B = "/api/v1/correo";

export const useTableroCorreo = (dias: number) =>
  useQuery({
    queryKey: ["correo", "tablero", dias],
    queryFn: () => api<TableroCorreo>(`${B}/tablero?dias=${dias}`),
    refetchInterval: 60_000,
  });

export const useBandejaCorreo = () =>
  useQuery({
    queryKey: ["correo", "bandeja"],
    queryFn: () => api<FilaBandeja[]>(`${B}/bandeja`),
    refetchInterval: 60_000,
  });

export const useDetalleCorreo = (id: string | null) =>
  useQuery({
    queryKey: ["correo", "detalle", id],
    queryFn: () => api<DetalleCorreo>(`${B}/bandeja/${id}`),
    enabled: id !== null,
  });

export const useConfigCorreo = () =>
  useQuery({
    queryKey: ["correo", "configuracion"],
    queryFn: () => api<ConfigCorreo>(`${B}/configuracion`),
  });

// ── Configuración editable (091) ────────────────────────────────────────────

export interface Remitente {
  id: string;
  patron: string;
  lista: "blanca" | "negra" | "registrar";
  casos: string[];
  notas: string | null;
  actualizado_por: string | null;
  updated_at: string;
}

export interface AjustesCorreo {
  casos_habilitados: string[];
  srps_por_correo: string[];
  cc_por_caso: Record<string, string[]>;
  tamano_max_kb: number;
  adjuntos: "aceptar" | "registrar" | "descartar";
  asunto_descartar: string[];
}

export interface FilaEscalacion {
  id: string;
  area: string;
  cliente: string | null;
  caso: string | null;
  nombre: string;
  responsables: string[];
  canal: "correo" | "telegram" | "ambos";
  minutos_respuesta: number | null;
  minutos_escalar: number | null;
  activo: boolean;
  notas: string | null;
}

export interface PlantillaCorreo {
  id: string;
  clave: string;
  caso: string | null;
  asunto: string;
  cuerpo: string;
  activa: boolean;
}

export interface DominioPorRevisar {
  dominio: string;
  correos: number;
  primero: string;
  ultimo: string;
  asunto_muestra: string;
  remitentes: string[];
  sugerencia: {
    tipo: "basura" | "cliente" | "proveedor" | "interno";
    motivos: string[];
  };
}

export interface SugerenciaCliente {
  dominio: string;
  cliente: string;
  contactos: number;
}

export interface CambioAuditado {
  id: string;
  ts: string;
  usuario: string;
  entidad: string;
  clave: string;
  accion: string;
}

const C = `${B}/config`;
const useConsulta = <T>(clave: string, ruta: string) =>
  useQuery({
    queryKey: ["correo", "config", clave],
    queryFn: () => api<T>(`${C}/${ruta}`),
  });

export const useRemitentes = () =>
  useConsulta<Remitente[]>("remitentes", "remitentes");
export const useAjustesCorreo = () =>
  useConsulta<AjustesCorreo>("ajustes", "ajustes");
export const useEscalacion = () =>
  useConsulta<FilaEscalacion[]>("escalacion", "escalacion");
export const usePlantillas = () =>
  useConsulta<PlantillaCorreo[]>("plantillas", "plantillas");
export const usePorRevisar = () =>
  useConsulta<DominioPorRevisar[]>("por_revisar", "por_revisar");
export const useSugerencias = () =>
  useConsulta<SugerenciaCliente[]>("sugerencias", "sugerencias");
export const useAuditoriaCorreo = () =>
  useConsulta<CambioAuditado[]>("auditoria", "auditoria?limite=50");

/** Una escritura de configuración: invalida todo lo de correo al terminar. */
export function useEscribirCorreo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      method: "POST" | "PUT" | "DELETE";
      ruta: string;
      body?: unknown;
    }) => api<unknown>(`${C}/${v.ruta}`, { method: v.method, body: v.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["correo"] }),
  });
}

export interface SimAnunciada {
  id: string;
  iccid: string | null;
  operador: string | null;
  agencia_texto: string;
  host: string | null;
  estado: string;
  motivo: string | null;
  fecha_instalacion: string | null;
  ultima_lectura_at: string | null;
  aprobacion_id: string | null;
  de: string | null;
  creada: string;
}

export const useSimsAnunciadas = () =>
  useQuery({
    queryKey: ["correo", "sims"],
    queryFn: () => api<SimAnunciada[]>(`${B}/sims`),
    refetchInterval: 60_000,
  });
